import { type ReactElement, type ReactNode } from 'react';
import { EFFECT_CLASS } from '@vello/tokens';

/**
 * Small parts of the Fleet console and the Central desk (D553 FLEET-CENTRAL).
 */

/**
 * The roster row that needs attention (documents expiring or missing): put it in the row's first cell and the `ListTable` row
 * takes the amber tint and the 3px rail (a CSS `:has`). The badges in the row carry the same fact in words, so colour is never alone.
 */
export function FleetCentralAttention(): ReactElement {
  return <span className="fleet-central-attention" aria-hidden="true" />;
}

export interface FleetCentralAuthLinkProps {
  readonly href: string;
  /** Called on click instead of following the href (the console's own router navigates). */
  readonly onActivate: () => void;
  /** Where the link sits in its row: `end` (under a form) or `center`. Default `center`. */
  readonly align?: 'end' | 'center';
  /** `label` (14px, the form's forgot link) or `caption` (13px, the back link). Default `caption`. */
  readonly size?: 'label' | 'caption';
  readonly children: ReactNode;
}

/** The sign-in panel's text link ("Forgot password", "Back to sign-in"): a real anchor in a 48px box, aligned by its row. */
export function FleetCentralAuthLink({ href, onActivate, align = 'center', size = 'caption', children }: FleetCentralAuthLinkProps): ReactElement {
  return (
    <div className="fleet-central-auth-link" data-align={align} data-size={size}>
      <a
        href={href}
        className="vello-link"
        onClick={(e) => {
          e.preventDefault();
          onActivate();
        }}
      >
        {children}
      </a>
    </div>
  );
}

/** The fixed-width slot a sparkline needs inside a `StatCard`'s `trend` (a chart measures its container; the card gives the slot none). */
export function FleetCentralTrend({ children }: { readonly children: ReactNode }): ReactElement {
  return <span className="fleet-central-trend">{children}</span>;
}

/** One record of an audit trail: a primary line and a quiet detail line, under a 3px rail. */
export function FleetCentralRecord({ primary, detail }: { readonly primary: ReactNode; readonly detail: ReactNode }): ReactElement {
  return (
    <div className="fleet-central-record">
      <div className="fleet-central-record__primary">{primary}</div>
      <div className="fleet-central-record__detail">{detail}</div>
    </div>
  );
}

export interface FleetCentralStepProps {
  /** The step's number (1-based), drawn in the circle. */
  readonly index: number;
  readonly state: 'done' | 'current' | 'pending';
  /** The step's name. */
  readonly children: ReactNode;
  /** After the name: the state as a Badge (never colour alone). */
  readonly trailing?: ReactNode;
}

/** A row of a numbered runbook: the number in a circle (tinted by state), the step, the state badge. Put several in a `Stack as="ol"`. */
export function FleetCentralStep({ index, state, children, trailing }: FleetCentralStepProps): ReactElement {
  return (
    <li className="fleet-central-step" data-state={state}>
      <span className="fleet-central-step__dot" aria-hidden="true">
        {index}
      </span>
      <span className="fleet-central-step__name">{children}</span>
      {trailing}
    </li>
  );
}

/** The agent's availability. */
export type FleetCentralPresence = 'online' | 'away' | 'wrap_up' | 'offline';

/** The presence dot beside the status pill: decoration only (the pill says the status in words), `online` pulses (the live-dot effect). */
export function FleetCentralPresenceDot({ presence }: { readonly presence: string }): ReactElement {
  return <span aria-hidden="true" className={`fleet-central-presence${presence === 'online' ? ` ${EFFECT_CLASS.liveDot}` : ''}`} data-presence={presence} />;
}

/** The one-line subtitle under the sign-in panel's heading (recovery). */
export function FleetCentralAuthSub({ children }: { readonly children: ReactNode }): ReactElement {
  return <p className="fleet-central-auth-sub">{children}</p>;
}
